<template>
  <modal
    name="Print"
    :height="'auto'"
    :width="520"
    :adaptive="true"
    :scrollable="true"
    :click-to-close="!isPrinting"
    classes="v--modal print-modal-shell"
    class="print-modal"
    @before-close="beforeClose"
    @closed="onClosed"
  >
    <div class="print-modal-content">
      <div class="print-modal-header">
        <div>
          <h2>Exportación de mapa</h2>
          <p>{{ isScreenCapture ? 'Capture exactamente lo que está viendo.' : 'Configure la presentación del archivo de salida.' }}</p>
        </div>
        <div class="print-modal-header-actions">
          <button
            type="button"
            class="control-btn"
            :title="isPrinting ? 'Espere a que termine la exportación' : 'Cerrar'"
            aria-label="Cerrar"
            :disabled="isPrinting"
            @click="hideModal()"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <line x1="5" y1="5" x2="19" y2="19" />
              <line x1="19" y1="5" x2="5" y2="19" />
            </svg>
          </button>
        </div>
      </div>

      <div class="print-modal-body">
        <div class="export-mode-row">
          <div class="form-group export-mode-group">
            <label for="export-mode">Método de exportación</label>
            <select id="export-mode" v-model="exportMode" :disabled="isPrinting">
              <option value="print">Composición de mapa</option>
              <option value="screen">Captura de pantalla</option>
            </select>
          </div>
          <button
            type="button"
            class="mode-help-button"
            aria-label="Explicar métodos de exportación"
            aria-controls="export-mode-help"
            :aria-expanded="showModeHelp ? 'true' : 'false'"
            :disabled="isPrinting"
            @click="showModeHelp = !showModeHelp"
          >?</button>
        </div>

        <div v-if="showModeHelp" id="export-mode-help" class="mode-help" role="note">
          <strong>¿Cuál es la diferencia?</strong>
          <span>
            La exportación cartográfica obtiene una vista limpia, pero debe procesar y componer las capas mediante el servidor;
            por eso consume más recursos y puede tardar más cuando existen varias capas activas. Captura de pantalla utiliza
            directamente lo que ya se ve en el visor, con HUD y paneles abiertos, por lo que normalmente es más rápida y
            liviana. Prefiera la captura si trabaja con capas internas o importadas (como KMZ/SHP), si alguna capa no aparece
            correctamente al exportar o si necesita procesar muchas capas. En ambos métodos puede descargar la imagen
            como PNG o incorporarla al PDF estructurado.
          </span>
        </div>

        <div v-if="isScreenCapture" class="print-alert print-alert-info capture-guidance" role="status">
          <strong>La imagen incluirá todo lo visible en el visor</strong>
          <span>
            Antes de capturar, deje abiertos solamente los paneles que desea conservar. Cuando el navegador lo solicite,
            seleccione la pestaña actual del Visor GRD. Elija PNG para descargarla sola o PDF para incorporarla al reporte.
            En la previsualización del navegador puede aparecer temporalmente este modal, pero no se incluirá en el archivo
            final. Esta función requiere HTTPS o localhost.
          </span>
        </div>

        <div v-if="captureErrorMessage" class="print-alert print-alert-error capture-error" role="alert">
          <strong>No fue posible capturar la pantalla</strong>
          <span>{{ captureErrorMessage }}</span>
        </div>

        <div v-if="!isScreenCapture" class="print-alert print-alert-info" role="note">
          <strong>Simbología utilizada en la exportación</strong>
          <span>
            Se utilizará exclusivamente la simbología original publicada por los servicios cartográficos.
            Las simbologías personalizadas seleccionadas en el visor no se incorporarán. Para conservar
            exactamente los estilos visibles, utilice Captura de pantalla.
          </span>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label for="print-template">Formato de papel</label>
            <select id="print-template" v-model="template" :disabled="isPrinting || printIsImageOnly">
              <option value="carta">Carta — 27,9 × 21,6 cm</option>
              <option value="A4">A4 — 29,7 × 21 cm</option>
              <option value="tabloide">Tabloide — 43,2 × 27,9 cm</option>
            </select>
            <small class="field-help">
              {{ printIsImageOnly ? 'Solo se utiliza para el PDF estructurado.' : 'Todos los formatos se generan en orientación horizontal.' }}
            </small>
          </div>

          <div class="form-group">
            <label for="print-format">Formato de salida</label>
            <select id="print-format" v-model="format" :disabled="isPrinting">
              <option value="pdf">PDF estructurado</option>
              <option value="png">PNG — solo imagen</option>
            </select>
          </div>
        </div>

        <div class="form-group" :class="{ 'form-group-disabled': printIsImageOnly }">
          <label for="print-title">Título</label>
          <input
            id="print-title"
            v-model="title"
            type="text"
            placeholder="Ingrese un título (opcional)"
            :disabled="isPrinting || printIsImageOnly"
          />
        </div>

        <div class="form-group" :class="{ 'form-group-disabled': printIsImageOnly }">
          <label for="print-subtitle">Subtítulo</label>
          <input
            id="print-subtitle"
            v-model="subtitle"
            type="text"
            placeholder="Ingrese un subtítulo (opcional)"
            :disabled="isPrinting || printIsImageOnly"
          />
        </div>

        <div class="legend-section" :class="{ active: legend && !isScreenCapture, disabled: isScreenCapture }">
          <label for="print-legend" class="legend-toggle">
            <span>
              <strong>Incluir leyenda</strong>
              <small>
                {{ isScreenCapture
                  ? 'No disponible en captura. Si la necesita, deje abierto el panel de leyenda antes del pantallazo.'
                  : 'Agrega la simbología de las capas compatibles.' }}
              </small>
            </span>
            <input
              id="print-legend"
              v-model="legend"
              type="checkbox"
              :disabled="isPrinting || isScreenCapture"
            />
          </label>

          <div v-if="legend" class="form-group legend-position">
            <label for="print-legend-position">Posición de la leyenda</label>
            <select
              id="print-legend-position"
              v-model="legendPosition"
              :disabled="isPrinting || isScreenCapture"
            >
              <option value="top-left">Superior izquierda</option>
              <option value="top-right">Superior derecha</option>
              <option value="bottom-left">Inferior izquierda</option>
              <option value="bottom-right">Inferior derecha</option>
            </select>
          </div>
        </div>

        <div v-if="!isScreenCapture && format === 'png'" class="print-alert print-alert-info" role="status">
          <strong>PNG sin plantilla</strong>
          <span>El archivo contendrá solamente el mapa y, si la activa, su leyenda. No incluirá título, subtítulo, fecha ni formato de papel.</span>
        </div>

        <div v-if="exceedsPrintLayerLimit" class="print-alert print-alert-error" role="alert">
          <strong>No es posible exportar el mapa</strong>
          <span>
            Hay {{ proxiedLayers.length }} capas que requieren procesamiento y el máximo permitido es
            {{ maxPrintLayers }}. Desactive al menos {{ proxiedLayers.length - maxPrintLayers }}
            {{ proxiedLayers.length - maxPrintLayers === 1 ? 'capa' : 'capas' }} para continuar.
          </span>
        </div>

        <div v-else-if="hasHighPrintLayerLoad" class="print-alert print-alert-warning" role="status">
          <strong>La exportación puede tardar más de lo normal</strong>
          <span>
            Se procesarán {{ proxiedLayers.length }} capas. Esta cantidad exige más recursos y puede
            aumentar el tiempo de generación.
          </span>
        </div>

        <div v-else-if="proxiedLayers.length && printState === 'idle'" class="print-alert print-alert-info" role="status">
          <strong>Capas procesadas al exportar</strong>
          <span>El servidor incluirá solamente las capas activas que requieren procesamiento: {{ proxiedLayers.join(', ') }}</span>
        </div>

        <div v-if="failedProxyLayerNames.length" class="print-alert print-alert-warning" role="alert">
          <strong>Capas no incluidas</strong>
          <span>No fue posible procesar: {{ failedProxyLayerNames.join(', ') }}</span>
        </div>

        <div v-if="omittedCanvasCount > 0" class="print-alert print-alert-warning" role="alert">
          <strong>Contenido omitido</strong>
          <span>
            Parte del mapa no pudo incluirse porque uno de los elementos renderizados
            no permitió leer sus píxeles.
            El proceso continuará con el contenido compatible.
          </span>
        </div>

        <div v-if="failedLegendLayerNames.length" class="print-alert print-alert-warning" role="alert">
          <strong>Leyendas no incluidas</strong>
          <span>No fue posible obtener la simbología de: {{ failedLegendLayerNames.join(', ') }}</span>
        </div>

        <div v-if="slowMessage" class="print-alert print-alert-info" role="status">
          La exportación está tardando más de lo normal. Esta ventana permanecerá abierta hasta que termine.
        </div>
      </div>

      <div class="print-modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="isPrinting" @click="hideModal()">Cancelar</button>
        <button
          v-if="printState === 'success' && docBlobUrl && format === 'pdf'"
          type="button"
          class="btn btn-preview"
          @click="openGeneratedDocument()"
        >
          Abrir PDF
        </button>
        <a
          v-if="printState === 'success' && docBlobUrl"
          :href="docBlobUrl"
          :download="downloadName"
          class="btn btn-download"
        >
          Descargar
        </a>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="isPrinting || exceedsPrintLayerLimit"
          @click="onClickExport()"
        >
          {{ primaryButtonLabel }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { vuex } from '@/components/mixin/vuex.js'
import {
  captureMap,
  buildLegendLayerRequests,
  buildMetadata,
  buildPrintContext,
  MAX_PROXY_LAYERS,
  proxiedLayerNames
} from './printRequest.js'
import { captureVisibleBrowserTab } from './screenCapture.js'

const PRINT_LAYER_WARNING_THRESHOLD = 6

export default {
  mixins: [vuex],
  data() {
    return {
      exportMode: 'print',
      showModeHelp: false,
      template: 'carta',
      legend: false,
      legendPosition: 'top-right',
      title: '',
      subtitle: '',
      format: 'pdf',
      mapName: '',
      printState: 'idle', // 'idle' | 'printing' | 'success'
      docBlobUrl: null,
      generatedAt: null,
      omittedCanvasCount: 0,
      proxyWarnings: [],
      legendWarnings: [],
      captureErrorMessage: '',
      slowMessage: false,
      softTimer: null,
      mapNameRequestId: 0
    }
  },
  computed: {
    isActive: function () {
      return this.mapToolValues('print').active
    },
    isPrinting: function () {
      return this.printState === 'printing'
    },
    isScreenCapture: function () {
      return this.exportMode === 'screen'
    },
    printIsImageOnly: function () {
      return this.format === 'png'
    },
    primaryButtonLabel: function () {
      if (this.isPrinting) return this.isScreenCapture ? 'Capturando…' : 'Generando…'
      return this.isScreenCapture ? 'Capturar pantalla' : 'Exportar'
    },
    maxPrintLayers: function () {
      return MAX_PROXY_LAYERS
    },
    exceedsPrintLayerLimit: function () {
      return !this.isScreenCapture && this.proxiedLayers.length > MAX_PROXY_LAYERS
    },
    hasHighPrintLayerLoad: function () {
      return !this.isScreenCapture &&
        this.proxiedLayers.length >= PRINT_LAYER_WARNING_THRESHOLD &&
        !this.exceedsPrintLayerLimit
    },
    downloadName: function () {
      const generatedAt = this.generatedAt || new Date()
      const pad = value => String(value).padStart(2, '0')
      const date = [
        generatedAt.getFullYear(),
        pad(generatedAt.getMonth() + 1),
        pad(generatedAt.getDate())
      ].join('-')
      const time = [pad(generatedAt.getHours()), pad(generatedAt.getMinutes())].join('-')
      if (this.isScreenCapture && this.format === 'png') return `captura_visor_${date}_${time}.png`
      if (this.isScreenCapture) return `reporte_captura_visor_${date}_${time}.pdf`
      if (this.format === 'png') return `mapa_cartografico_${date}_${time}.png`
      return `exportacion_mapa_${date}_${time}.${this.format}`
    },
    // Solo estas capas activas se reemplazan temporalmente por una imagen
    // obtenida a traves del backend. El mapa normal no cambia.
    proxiedLayers: function () {
      return proxiedLayerNames(this.activeLayers)
    },
    failedProxyLayerNames: function () {
      return [...new Set(this.proxyWarnings
        .map(warning => warning.layerName)
        .filter(name => name && name !== 'Mapa'))]
    },
    failedLegendLayerNames: function () {
      return [...new Set(this.legendWarnings
        .filter(warning => warning && warning.kind === 'legend')
        .map(warning => warning.layer_name)
        .filter(Boolean))]
    }
  },
  methods: {
    showModal: function () {
      this.$modal.show('Print')
    },
    hideModal: function () {
      if (this.isPrinting) return
      this.resetPrintState()
      this.$modal.hide('Print')
    },
    beforeClose: function (event) {
      if (this.isPrinting && event && typeof event.stop === 'function') event.stop()
    },
    // Sincroniza el store al cerrar (Cancelar/backdrop) — el patrón muerto de Export.vue lo omitía.
    onClosed: function () {
      this.resetPrintState()
      this.selectMapTool('print', false)
    },
    clearTimers: function () {
      clearTimeout(this.softTimer)
      this.softTimer = null
    },
    // Única limpieza del modal: configuración, resultados y solicitudes pendientes.
    resetPrintState: function () {
      this.clearTimers()
      this.mapNameRequestId += 1
      if (this.docBlobUrl) {
        URL.revokeObjectURL(this.docBlobUrl)
        this.docBlobUrl = null
      }
      this.exportMode = 'print'
      this.showModeHelp = false
      this.template = 'carta'
      this.legend = false
      this.legendPosition = 'top-right'
      this.title = ''
      this.subtitle = ''
      this.format = 'pdf'
      this.mapName = ''
      this.printState = 'idle'
      this.generatedAt = null
      this.omittedCanvasCount = 0
      this.proxyWarnings = []
      this.legendWarnings = []
      this.captureErrorMessage = ''
      this.slowMessage = false
    },
    clearPreviousResult: function () {
      if (this.docBlobUrl) {
        URL.revokeObjectURL(this.docBlobUrl)
        this.docBlobUrl = null
      }
      this.generatedAt = null
    },
    // Cualquier cambio posterior a un resultado obliga a generar el archivo
    // nuevamente. Se conserva la configuracion que acaba de elegir el usuario,
    // pero se eliminan el documento y los avisos de la ejecucion anterior.
    invalidateGeneratedResult: function () {
      if (this.isPrinting) return
      this.clearTimers()
      this.clearPreviousResult()
      this.printState = 'idle'
      this.omittedCanvasCount = 0
      this.proxyWarnings = []
      this.legendWarnings = []
      this.captureErrorMessage = ''
      this.slowMessage = false
    },
    setPrintModalVisible: function (visible) {
      const modal = document.querySelector('[data-modal="Print"]')
      if (modal) modal.style.visibility = visible ? '' : 'hidden'
    },
    waitForInterfacePaint: function () {
      return new Promise(resolve => {
        this.$nextTick(() => {
          window.requestAnimationFrame(() => window.requestAnimationFrame(resolve))
        })
      })
    },
    // base64 → Blob → objectURL (documento efímero).
    toBlobUrl: function (b64, mime) {
      const bytes = atob(b64)
      const arr = new Uint8Array(bytes.length)
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
      return URL.createObjectURL(new Blob([arr], { type: mime }))
    },
    blobToDataUrl: function (blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(new Error('No fue posible preparar la captura para el reporte.'))
        reader.readAsDataURL(blob)
      })
    },
    openPendingPdfWindow: function () {
      if (this.format !== 'pdf') return null
      const previewWindow = window.open('', '_blank')
      if (!previewWindow) return null
      previewWindow.document.title = 'Generando PDF · Visor GRD'
      previewWindow.document.body.innerHTML = `
        <main style="min-height:100vh;display:flex;align-items:center;justify-content:center;
          box-sizing:border-box;background:#f4f6f8;color:#0d2d44;font-family:Arial,sans-serif">
          <section style="max-width:420px;padding:36px;text-align:center">
            <div style="font-size:30px;font-weight:700;color:#0877bd">Visor
              <span style="color:#ef7900">GRD</span>
            </div>
            <p style="margin:20px 0 6px;font-size:18px;font-weight:700">Generando PDF…</p>
            <p style="margin:0;color:#667785;line-height:1.5">
              Esta pestaña mostrará el documento cuando termine la exportación.
            </p>
          </section>
        </main>`
      return previewWindow
    },
    openGeneratedDocument: function () {
      if (!this.docBlobUrl) return
      const previewWindow = window.open(this.docBlobUrl, '_blank')
      if (!previewWindow) {
        this.$store.dispatch('toast/show', {
          message: 'El navegador bloqueó la pestaña. Permita ventanas emergentes o use Descargar.',
          type: 'warning'
        })
      }
    },
    failPrint: function (error) {
      if (this.printState !== 'printing') return
      this.clearTimers()
      console.error('[print]', error) // detalle SOLO a consola
      this.printState = 'idle'
      this.slowMessage = false
      // El backend distingue geoprocess caído (503), capa no autorizada (403) y
      // payload demasiado grande (413) con un mensaje propio; sin esto todos
      // llegaban al usuario como el mismo error genérico.
      const backendMessage = error && error.response && error.response.data && error.response.data.message
      this.$store.dispatch('toast/show',
        { message: backendMessage || 'No se pudo exportar el mapa. Intente nuevamente.', type: 'error' })
    },
    failScreenCapture: function (error) {
      if (this.printState !== 'printing') return
      this.clearTimers()
      console.error('[screen-capture]', error)
      this.printState = 'idle'
      this.slowMessage = false
      let message = error && error.message
        ? error.message
        : 'No se pudo generar la captura de pantalla. Revise los permisos del navegador e intente nuevamente.'
      if (error && error.name === 'NotAllowedError') {
        message = 'El permiso fue cancelado o bloqueado. Presione “Capturar pantalla” nuevamente, autorice el acceso y seleccione la pestaña actual del Visor GRD.'
      } else if (error && error.name === 'NotReadableError') {
        message = 'El navegador recibió el permiso, pero el sistema no pudo iniciar la captura. Detenga otras grabaciones o aplicaciones que estén compartiendo pantalla e intente nuevamente.'
      } else if (error && error.name === 'AbortError') {
        message = 'La captura se interrumpió antes de obtener la imagen. Intente nuevamente y mantenga abierta la pestaña del visor.'
      } else if (error && error.name === 'InvalidStateError') {
        message = 'El navegador rechazó la captura porque la pestaña no estaba activa. Vuelva al visor y presione el botón nuevamente.'
      }
      this.captureErrorMessage = message
      this.$store.dispatch('toast/show', { message, type: 'error' })
    },
    fetchMapName: function () {
      const idMap = this.$route.params.idMap
      if (!idMap) return
      const requestId = ++this.mapNameRequestId
      this.$http.get(this.$store.getters.backendurl + 'api/v1/maps/' + idMap)
        .then(response => {
          if (requestId === this.mapNameRequestId && this.isActive) {
            this.mapName = response.data.name
          }
        })
        .catch(error => { console.error('[print] no se pudo obtener el nombre del mapa', error) })
    },
    onClickExport: function () {
      if (this.isScreenCapture) return this.onClickScreenCapture()
      return this.onClickPrint()
    },
    onClickScreenCapture: async function () {
      if (this.printState === 'printing') return
      this.clearPreviousResult()
      this.printState = 'printing'
      this.omittedCanvasCount = 0
      this.proxyWarnings = []
      this.legendWarnings = []
      this.captureErrorMessage = ''
      this.slowMessage = false
      try {
        const blob = await captureVisibleBrowserTab({
          beforeFrame: async () => {
            this.setPrintModalVisible(false)
            await this.waitForInterfacePaint()
          },
          afterFrame: async () => {
            this.setPrintModalVisible(true)
            await this.waitForInterfacePaint()
          }
        })
        if (this.printState !== 'printing') return
        if (this.format === 'png') {
          this.docBlobUrl = URL.createObjectURL(blob)
          this.generatedAt = new Date()
          this.printState = 'success'
          this.captureErrorMessage = ''
          this.$store.dispatch('toast/show', {
            message: 'Captura de pantalla generada',
            type: 'success'
          })
          return
        }

        this.softTimer = setTimeout(() => { this.slowMessage = true }, 60000)
        const mapId = this.$route.params.idMap
        const mapImage = await this.blobToDataUrl(blob)
        const response = await this.$http.post(
          this.$store.getters.backendurl + 'api/v1/print',
          {
            map_image: mapImage,
            title: this.title,
            subtitle: this.subtitle,
            map_id: mapId,
            template: this.template,
            legend: false,
            legend_position: this.legendPosition,
            scale_bar: null,
            legend_layer_requests: [],
            format: 'pdf',
            capture_mode: 'screen',
            print_context: buildPrintContext({
              mapName: this.mapName,
              activeLayers: this.activeLayers,
              geoprocessData: this.mapToolValues('geoprocessing').data,
              proxyWarnings: []
            }),
            metadata: buildMetadata({
              mapName: this.mapName,
              activeLayers: this.activeLayers,
              geoprocessData: this.mapToolValues('geoprocessing').data,
              proxyWarnings: []
            })
          }
        )
        if (this.printState !== 'printing') return
        this.clearTimers()
        this.legendWarnings = Array.isArray(response.data.warnings) ? response.data.warnings : []
        this.docBlobUrl = this.toBlobUrl(response.data.document, response.data.mime)
        this.generatedAt = new Date()
        this.printState = 'success'
        this.captureErrorMessage = ''
        this.slowMessage = false
        this.$store.dispatch('toast/show', {
          message: 'PDF estructurado generado a partir de la captura',
          type: 'success'
        })
      } catch (error) {
        this.setPrintModalVisible(true)
        this.failScreenCapture(error)
      }
    },
    onClickPrint: function () {
      if (this.printState === 'printing') return // guard doble-envío
      if (!this.template || !this.format) return
      if (this.exceedsPrintLayerLimit) {
        this.$store.dispatch('toast/show', {
          message: `No se puede exportar un mapa con más de ${MAX_PROXY_LAYERS} capas que requieran procesamiento.`,
          type: 'error'
        })
        return
      }
      if (this.hasHighPrintLayerLoad) {
        this.$store.dispatch('toast/show', {
          message: `Se procesarán ${this.proxiedLayers.length} capas. La exportación puede tardar más de lo normal.`,
          type: 'warning'
        })
      }
      this.clearPreviousResult()
      // La pestaña debe crearse de forma sincrónica con el clic. Al terminar se
      // reemplaza su pantalla de espera por el PDF, sin depender de un popup asíncrono.
      const previewWindow = this.openPendingPdfWindow()
      this.printState = 'printing'
      this.omittedCanvasCount = 0
      this.proxyWarnings = []
      this.legendWarnings = []
      this.slowMessage = false
      this.softTimer = setTimeout(() => { this.slowMessage = true }, 60000)
      const mapId = this.$route.params.idMap
      captureMap({
        mapId,
        activeLayers: this.activeLayers,
        requestLayerImage: ({ layerId, payload }) => this.$http.post(
          this.$store.getters.backendurl + 'api/v1/print/layers/' + encodeURIComponent(layerId) + '/image',
          payload,
          { responseType: 'blob' }
        )
      })
        .then(({ mapImage, omittedCanvasCount, proxyWarnings, scaleBar }) => {
          if (this.printState !== 'printing') throw new Error('print-cancelled')
          this.omittedCanvasCount = omittedCanvasCount
          this.proxyWarnings = proxyWarnings
          const payload = {
            map_image: mapImage,
            title: this.format === 'pdf' ? this.title : '',
            subtitle: this.format === 'pdf' ? this.subtitle : '',
            map_id: mapId,
            template: this.template,
            legend: this.legend,
            legend_position: this.legendPosition,
            scale_bar: this.format === 'pdf' ? scaleBar : null,
            legend_layer_requests: this.legend
              ? buildLegendLayerRequests(this.activeLayers, proxyWarnings)
              : [],
            format: this.format,
            print_context: buildPrintContext({
              mapName: this.mapName,
              activeLayers: this.activeLayers,
              geoprocessData: this.mapToolValues('geoprocessing').data,
              proxyWarnings
            }),
            metadata: buildMetadata({
              mapName: this.mapName,
              activeLayers: this.activeLayers,
              geoprocessData: this.mapToolValues('geoprocessing').data,
              proxyWarnings
            })
          }
          return this.$http.post(this.$store.getters.backendurl + 'api/v1/print', payload)
        })
        .then(response => {
          if (this.printState !== 'printing') { if (previewWindow) previewWindow.close(); return }
          this.clearTimers()
          this.legendWarnings = Array.isArray(response.data.warnings) ? response.data.warnings : []
          this.docBlobUrl = this.toBlobUrl(response.data.document, response.data.mime)
          this.generatedAt = new Date()
          this.printState = 'success'
          this.slowMessage = false
          if (previewWindow) previewWindow.location.replace(this.docBlobUrl)
          const hasOmittedContent = this.proxyWarnings.length > 0 ||
            this.omittedCanvasCount > 0 || this.legendWarnings.length > 0
          this.$store.dispatch('toast/show', hasOmittedContent
            ? {
                message: 'Mapa exportado. Algunas capas o leyendas no pudieron incluirse.',
                type: 'warning'
              }
            : { message: 'Mapa exportado', type: 'success' })
        })
        .catch(error => {
          if (previewWindow) previewWindow.close()
          this.failPrint(error)
        })
    }
  },
  watch: {
    exportMode: function (newMode, oldMode) {
      if (newMode === oldMode) return
      this.invalidateGeneratedResult()
      if (newMode === 'screen') {
        this.legend = false
      }
    },
    format: function (newFormat, oldFormat) {
      if (newFormat === oldFormat || this.isPrinting) return
      this.invalidateGeneratedResult()
    },
    template: 'invalidateGeneratedResult',
    legend: 'invalidateGeneratedResult',
    legendPosition: 'invalidateGeneratedResult',
    title: 'invalidateGeneratedResult',
    subtitle: 'invalidateGeneratedResult',
    isActive: function (newValue) {
      if (newValue) {
        this.resetPrintState()
        this.fetchMapName()
        this.showModal()
      } else {
        // Una desactivación externa (por ejemplo, reiniciar o cambiar el mapa)
        // debe limpiar incluso si la exportación estaba en curso. Los botones del
        // modal continúan bloqueados durante la generación mediante hideModal().
        this.resetPrintState()
        this.$modal.hide('Print')
      }
    }
  },
  beforeDestroy: function () {
    this.setPrintModalVisible(true)
    this.resetPrintState()
  }
}
</script>

<style scoped>
.print-modal::v-deep .print-modal-shell {
  overflow: hidden;
  background: #fff;
  border: 0;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.print-modal-content {
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 0;
  border-radius: 0;
  color: #2c3e50;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.print-modal-header {
  padding: 18px 24px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  background: var(--color-navy);
}

.print-modal-header h2 {
  margin: 0;
  color: var(--color-white, #fff);
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
}

.print-modal-header p {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.88rem;
  line-height: 1.4;
}

.print-modal-header-actions {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
}

.print-modal-header-actions .control-btn {
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.print-modal-header-actions .control-btn:hover {
  background: rgba(255, 255, 255, 0.16);
  color: var(--color-white, #fff);
}

.print-modal-header-actions .control-btn:disabled {
  background: transparent;
  color: rgba(255, 255, 255, 0.35);
  cursor: not-allowed;
}

.print-modal-header-actions .control-btn:focus-visible {
  outline: 2px solid var(--color-orange);
  outline-offset: 2px;
}

.print-modal-header-actions svg {
  width: 11px;
  height: 11px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.print-modal-body {
  padding: 22px 24px 24px;
  overflow-y: auto;
  background: #fafafa;
}

.export-mode-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.export-mode-group {
  flex: 1 1 auto;
}

.mode-help-button {
  width: 34px;
  height: 34px;
  margin-bottom: 18px;
  flex: 0 0 34px;
  border: 1px solid var(--color-line, #d8dde3);
  border-radius: 50%;
  background: #fff;
  color: var(--color-navy);
  cursor: pointer;
  font-size: 1rem;
  font-weight: 800;
}

.mode-help-button:hover:not(:disabled),
.mode-help-button:focus-visible {
  border-color: var(--color-orange);
  color: var(--color-orange);
  outline: none;
  box-shadow: 0 0 0 3px rgba(234, 122, 41, 0.14);
}

.mode-help-button:disabled {
  background: #f0f1f2;
  color: #8a8f94;
  cursor: not-allowed;
}

.mode-help {
  margin: -6px 0 18px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #b9d9ec;
  border-radius: 8px;
  background: #eef8fd;
  color: #245d78;
  font-size: 0.82rem;
  line-height: 1.45;
}

.capture-guidance {
  margin: 0 0 18px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  margin-bottom: 7px;
  color: #34495e;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.35px;
  text-transform: uppercase;
}

.form-group input,
.form-group select {
  width: 100%;
  min-height: 42px;
  padding: 9px 12px;
  border: 1px solid var(--color-line, #d8dde3);
  border-radius: 6px;
  background: #fff;
  color: #2c3e50;
  font: inherit;
  font-size: 0.92rem;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input::placeholder {
  color: #a0a7ae;
}

.form-group input:focus,
.form-group select:focus {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 3px rgba(234, 122, 41, 0.14);
  outline: none;
}

.form-group input:disabled,
.form-group select:disabled {
  background: #f0f1f2;
  color: #8a8f94;
  cursor: not-allowed;
}

.form-group-disabled label {
  color: #8a8f94;
}

.field-help {
  display: block;
  margin-top: 6px;
  color: #7f8c8d;
  font-size: 0.75rem;
  line-height: 1.35;
}

.legend-section {
  margin-top: 2px;
  padding: 14px 16px;
  border: 2px solid var(--color-line, #e2e5e8);
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.2s, background 0.2s;
}

.legend-section.active {
  border-color: var(--color-orange);
  background: var(--color-orange-light, #fff7f1);
}

.legend-section.disabled {
  border-color: var(--color-line, #e2e5e8);
  background: #f0f1f2;
  opacity: 0.72;
}

.legend-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: 0;
  cursor: pointer;
}

.legend-toggle span {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.legend-toggle strong {
  color: #2c3e50;
  font-size: 0.94rem;
}

.legend-toggle small {
  color: #7f8c8d;
  font-size: 0.8rem;
  line-height: 1.35;
}

.legend-toggle input {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  accent-color: var(--color-orange);
  cursor: pointer;
}

.legend-toggle input:disabled {
  cursor: not-allowed;
}

.legend-position {
  margin: 16px 0 0;
  padding-top: 16px;
  border-top: 1px solid #eadfd6;
}

.print-alert {
  margin-top: 16px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  border-radius: 8px;
  font-size: 0.84rem;
  line-height: 1.45;
}

.print-alert-warning {
  border: 1px solid #f1d18a;
  background: #fff9e6;
  color: #7a5a11;
}

.print-alert-error {
  border: 1px solid #e6a5a5;
  background: #fff1f1;
  color: #8b2525;
}

.print-alert-info {
  border: 1px solid #b9d9ec;
  background: #eef8fd;
  color: #245d78;
}

.print-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  background: #fff;
}

.print-modal-footer .btn {
  min-width: 104px;
  min-height: 40px;
  padding: 9px 18px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  transition: background 0.2s, color 0.2s, transform 0.2s, opacity 0.2s;
}

.print-modal-footer .btn-secondary {
  border: 0;
  background: #e0e0e0;
  color: #333;
}

.print-modal-footer .btn-secondary:hover {
  background: #d5d5d5;
  color: #222;
}

.print-modal-footer .btn-primary,
.print-modal-footer .btn-download {
  border: 0;
  background: var(--color-orange);
  color: var(--color-white, #fff);
}

.print-modal-footer .btn-preview {
  border: 1px solid var(--color-navy);
  background: var(--color-white, #fff);
  color: var(--color-navy);
}

.print-modal-footer .btn-preview:hover {
  background: var(--color-navy);
  color: var(--color-white, #fff);
  transform: translateY(-1px);
}

.print-modal-footer .btn-primary:hover:not(:disabled),
.print-modal-footer .btn-download:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.print-modal-footer .btn:disabled {
  border: 0;
  background: #d9dcdf;
  color: #7d8388;
  cursor: not-allowed;
  opacity: 1;
}

@media (max-width: 560px) {
  .print-modal-header,
  .print-modal-body,
  .print-modal-footer {
    padding-left: 18px;
    padding-right: 18px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .print-modal-footer {
    flex-wrap: wrap;
  }

  .print-modal-footer .btn {
    flex: 1 1 120px;
  }
}
</style>
